import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { CertificationShell } from "./CertificationShell";
import { RedirectingPage } from "./RedirectingPage";
import { CertificationHomePage } from "@/components/certification/CertificationHomePage";
import { CoursePage } from "@/components/certification/CoursePage";
import { QuizPage } from "./pages/QuizPage";
import { ResourcesPage } from "./pages/ResourcesPage";
import { getAuthCookie } from "@/lib/auth/session-cookie";
import {
  bounceToAccountLogin,
  haltForExternalNavigation,
} from "@/lib/auth/app-origins";

/**
 * Hand-authored route tree for the certification app — intentionally NOT
 * file-based codegen. This app is small and stable (home, course, training
 * quiz, resources), and keeping it code-based avoids sharing a routeTree.gen.ts
 * with the main dashboard app, which is the whole point of the isolation:
 * this app's router has zero knowledge of /workspace, /fleet, etc.
 *
 * There is deliberately no local login/register UI here — auth is handled
 * entirely by account-web (login, Google OAuth, registration/org-setup) to
 * avoid duplicating that logic. The auth token lives in a cookie shared
 * across the whole *.lionenergy.com domain in prod (or shared across ports
 * on plain localhost in local dev — see session-cookie.ts), so if you're
 * already signed into any Lion Pro app, this guard sees it directly with no
 * redirect at all. Only genuinely unauthenticated visitors get bounced to
 * account-web /login (with a `redirect` param to return here afterward).
 */

function hasAccessToken(): boolean {
  return !!getAuthCookie("access_token");
}

function requireCertificationAuth() {
  if (!hasAccessToken()) {
    bounceToAccountLogin(window.location.href);
    // Halt the router — do not throw redirect() (History API spam in Firefox).
    haltForExternalNavigation();
  }
}

const rootRoute = createRootRoute({
  component: CertificationShell,
});

const redirectingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/redirecting",
  component: RedirectingPage,
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: requireCertificationAuth,
  component: CertificationHomePage,
});

const courseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/course/$courseId",
  beforeLoad: requireCertificationAuth,
  component: () => {
    const { courseId } = courseRoute.useParams();
    return <CoursePage courseId={courseId} />;
  },
});

/** Legacy lesson URL — video now lives on the quiz route. */
const lessonRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/course/$courseId/lesson/$lessonId",
  beforeLoad: ({ params }) => {
    requireCertificationAuth();
    // Cast: certification routes aren't in the main app Register.router union.
    throw redirect({
      to: `/course/${params.courseId}/lesson/${params.lessonId}/quiz`,
      replace: true,
    } as any);
  },
});

const quizRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/course/$courseId/lesson/$lessonId/quiz",
  beforeLoad: requireCertificationAuth,
  component: () => {
    const { courseId, lessonId } = quizRoute.useParams();
    return <QuizPage courseId={courseId} lessonId={lessonId} />;
  },
});

const resourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/resources",
  beforeLoad: requireCertificationAuth,
  component: ResourcesPage,
});

const routeTree = rootRoute.addChildren([
  redirectingRoute,
  homeRoute,
  courseRoute,
  lessonRedirectRoute,
  quizRoute,
  resourcesRoute,
]);

export const certificationRouter = createRouter({ routeTree });

// Deliberately NOT declaring `Register.router` here — the main app's
// main.tsx already owns that global augmentation, and only one router's
// type can win it per TS program. This router still works fully at runtime
// via RouterProvider; it just means Link/useNavigate calls inside the
// certification app's own components aren't statically checked against
// this tree (see the `as any` casts on `to` props in CertificationHomePage
// and CoursePage for the routes that only exist in this tree).
